import { TFunction } from "i18next"
import { useFormRenderer } from "./hooks"
import {
  DataDrivenFormSchema,
  FormProperties,
  HashSection,
  PageField,
  ScrollingLinkSection,
} from "./schema"
import { useSectionErrors } from "./section-error-provider"
import React from "react"
import { Badge } from "@/components/ui/badge"

export function WithErrorIndicator({
  section,
  children,
}: {
  section: HashSection<any, PageField> | ScrollingLinkSection<any, PageField>
  children: React.ReactNode
}) {
  const { sectionErrors } = useSectionErrors()
  const { tFunc } = useFormRenderer() as { tFunc: TFunction<any> }
  const hasError = sectionErrors.has(section.sectionKey)
  return (
    <div className="flex flex-row items-center">
      {children}
      {hasError && (
        <div
          role="status"
          aria-label={tFunc("common:anchor.section-error", {
            sectionName: tFunc(section.title),
          })}
          className="bg-destructive size-4 rounded-full"
        />
      )}
    </div>
  )
}

export function ErrorBanner({
  schema,
}: {
  schema: DataDrivenFormSchema<any, PageField>
}) {
  const { sectionErrors } = useSectionErrors()

  const { tFunc } = useFormRenderer() as { tFunc: TFunction<any> }

  const sectionTitles = React.useMemo(() => {
    const sectionTitles: Set<string> = new Set()
    const flattenedSections: FormProperties<any, PageField>[] = [
      ...schema.properties,
    ]

    while (flattenedSections.length > 0) {
      const property = flattenedSections.pop()!

      if (
        property.type === "section:hash" ||
        property.type === "section:scrolling"
      ) {
        if (sectionErrors.has(property.sectionKey)) {
          sectionTitles.add(property.title)
        }

        if (property.properties) {
          flattenedSections.push(...property.properties)
        }
      }
    }
    return sectionTitles
  }, [sectionErrors, schema])

  if (sectionTitles.size === 0) {
    return null
  }

  const translatedSectionTitles = Array.from(sectionTitles)
    .slice(0, 2)
    .map((title) => tFunc(title))

  return (
    <Badge variant="destructive" className="w-full lg:text-base">
      {translatedSectionTitles.length <= 2
        ? tFunc("common:anchor.error-header.errors", {
            sections: translatedSectionTitles.join(", "),
          })
        : tFunc("common:anchor.error-header.errors_with_others", {
            sections: translatedSectionTitles.join(", "),
            otherCount: sectionTitles.size - 2,
          })}
    </Badge>
  )
}
